import {
    CUSTOMER_TYPE_DSL,
    CUSTOMER_TYPE_UM,
    CUSTOMER_TYPE_VKD,
    CustomerType,
} from '@vfde-sails/constants';
import { getCookie } from '@vfde-sails/utils';
import { MDDKeks } from '../interface';
import { Portfolio, PublicFactName } from '@vfde-sails/avc-core';
import { ProviderType } from '../enum';
import { getCMSConfig } from '../../../util/cms';
import { VFCookie } from '../../../enum';
import { debugInfo } from '../../../util/debug';

/**
 * Reads the `customerType` from the `MDDKeks` cookie.
 * and returns its value or null if not logged in.
 * @returns {CustomerType|null} Customer type of the customer, null if not logged in.
 */
export const getCustomerType = (): CustomerType | null => {
    const customerCookie = getCookie(VFCookie.MDDKEKS);

    if (!customerCookie) {
        return null;
    }

    try {
        const parsedCustomerCookie: MDDKeks = JSON.parse(decodeURIComponent(customerCookie));

        return parsedCustomerCookie ? parsedCustomerCookie.customerType : null;
    }
    catch (error) {
        debugInfo('Cannot parse MDDKeks cookie: ', error);
        return null;
    }
};

/**
 * Redirects the user based on the applied `publicFact`.
 * Does not redirect if non applies.
 * @param {PublicFactName} publicFactName - The name of the public fact that applies to the customer.
 */
export const handlePublicFactRedirects = (publicFactName: PublicFactName) => {
    const { redirectLinks } = getCMSConfig();

    switch (publicFactName) {
        case 'customer_has_a_non_supported_product':
            location.replace(redirectLinks.nonSupportedProduct);
            break;
        case 'customer_has_1play_phone':
            location.replace(redirectLinks.noKipContract);
            break;
        case 'customer_has_non_supported_customer_type':
            location.replace(redirectLinks.nonSupportedCustomer);
            break;
        case 'customer_is_in_dunning_status':
        case 'customer_has_a_3play_product':
        case 'customer_has_open_orders':
        case 'customer_at_ume_address_has_vkd_kud':
            location.replace(redirectLinks.changeUnavailable);
            break;
    }
};

/**
 * Returns the provider type based on customer type, redirects if not type of UM/VKD.
 * @param {CustomerType} customerType - The type of the customer
 * @returns {ProviderType} The provider type of the customer
 */
export const getProviderType = (customerType: CustomerType): ProviderType | null => {
    const { redirectLinks } = getCMSConfig();

    switch (true) {
        /* Customer is Cable customer */
        case customerType.includes(CUSTOMER_TYPE_UM):
            return ProviderType.UM;
        case customerType.includes(CUSTOMER_TYPE_VKD):
            return ProviderType.VKD;
        /* Redirect to DSL Customer page if customer is DSL */
        case customerType.includes(CUSTOMER_TYPE_DSL):
            location.replace(redirectLinks.dslCustomer);
            return null;
        default:
            debugInfo('Unexpected customer type: ', customerType);
            location.replace(redirectLinks.nonSupportedCustomer);
            return null;
    }
};

/**
 * Checks the gimli availability response and maps the products to their ids
 * Disallows availablity of routers if user already has better router.
 * @param {Portfolio} availability - The result of the gimli availablity call.
 * @param {string[]} inventoryMetacodes - The metacodes present in the users inventory
 * @returns {string[]} An array containing the available router ids.
 */
export const getAvailableRouterIds = (availability: Portfolio, inventoryMetacodes: string[]): string[] => {
    // If customer already has the best available router, he cannot change it
    if (Object.values(getCMSConfig().prioRouterIds).some(metacode => inventoryMetacodes.includes(metacode))) {
        return [];
    }

    return Object.entries(availability.products)
        .filter(([id, product]) => !inventoryMetacodes.includes(id) && product.available)
        .map(([id]) => id);
};
